import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deleteJSON, fetchJSON, postJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import {
  sharedCreateNewAvailability,
  sharedDeleteAvailability,
  sharedEditAvailability,
} from 'features/availability/sharedThunks';
// SHARED AVAILABILITY TYPES
import { AvailabilityTemplate } from 'features/availability/types';

import { toI18n } from 'util/i18n';

import { MY_AVAILABILITY_SLICE } from './constants';
import { selectSelectedTemplate } from './selectors';
// MY AVAILABILITY SPECIFIC TYPES
import { MyAvailabilityState } from './types';

const initialState: MyAvailabilityState = {
  data: null,
  selectedTemplateId: null,
  loading: true,
  isSubmittingNotifyManager: false,
  isSubmittingClearAvailability: false,
  isSubmittingCreateTemplate: false,
  error: null,
  availabilityError: null,
  isSubmittingAvailability: false,
};

// THUNKS
export const fetchUserAvailabilities = createAsyncThunk(
  `${MY_AVAILABILITY_SLICE}/fetchUserAvailabilities`,
  async () => fetchJSON('/user/availability.json')
);

export const createAvailabilityTemplate = createAsyncThunk(
  `${MY_AVAILABILITY_SLICE}/createAvailabilityTemplate`,
  async ({
    start_at,
    base_template_id,
  }: {
    start_at: string;
    base_template_id?: number;
  }) => {
    const params = {
      start_at,
      ...(base_template_id && { base_template_id }),
    };

    return postJSON('/availability_templates', params);
  }
);

export const clearAvailabilities = createAsyncThunk(
  `${MY_AVAILABILITY_SLICE}/clearAvailabilities`,
  async ({ templateId }: { templateId: number }) => {
    await deleteJSON(
      `/availability_templates/${templateId}/availabilities/clear`
    );
    return { templateId };
  }
);

export const notifyManager = createAsyncThunk(
  `${MY_AVAILABILITY_SLICE}/notifyManager`,
  async ({
    templateId,
    templateData,
    note,
  }: {
    templateId: number;
    templateData: AvailabilityTemplate;
    note: string;
  }) => {
    // Backend handles status transition based on publish flag
    // NOTE: In the future, we could make the publish flag conditional to keep status 'approved'
    // if this is just a note to the manager with no availability changes.
    // Example: publish: hasAvailabilityChanges
    const response = await putJSON(`/availability_templates/${templateId}`, {
      availability_template: {
        ...templateData,
        employee_notes: note,
        publish: true,
      },
    });
    return { response, note };
  }
);

export const updateTemplateStatus = createAsyncThunk(
  `${MY_AVAILABILITY_SLICE}/updateTemplateStatus`,
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { id } = selectSelectedTemplate(state);

    return putJSON(`/availability_templates/${id}`, {
      availability_template: { status: 'unsubmitted' },
    });
  }
);

// SHARED THUNKS
export const createNewAvailability = sharedCreateNewAvailability(
  MY_AVAILABILITY_SLICE
);
export const editAvailability = sharedEditAvailability(MY_AVAILABILITY_SLICE);
export const deleteAvailability = sharedDeleteAvailability(
  MY_AVAILABILITY_SLICE
);

const myAvailabilitySlice = createSlice({
  name: MY_AVAILABILITY_SLICE,
  initialState,
  reducers: {
    setSelectedTemplateId: (state, action: PayloadAction<number>) => {
      state.selectedTemplateId = action.payload;
    },
    resetAvailabilityError: state => {
      state.availabilityError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUserAvailabilities.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserAvailabilities.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;

        // Set the selected template ID if it's not already set
        if (state.selectedTemplateId === null) {
          const currentTemplate = action.payload.availability_templates.find(
            (template: AvailabilityTemplate) => template.current
          );

          // Current template will always exist per backend guarantees
          state.selectedTemplateId = currentTemplate.id;
        }
      })
      .addCase(fetchUserAvailabilities.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || 'Failed to fetch availability data';
      })
      .addCase(notifyManager.pending, state => {
        state.isSubmittingNotifyManager = true;
      })
      .addCase(notifyManager.fulfilled, (state, action) => {
        // Find the template to update after successful submission
        const templateToUpdate = state.data!.availability_templates.find(
          template => template.id === state.selectedTemplateId
        )!;

        // Update the template with the returned data
        templateToUpdate.employee_notes = action.payload.note;

        // Only set status to 'pending' if approval is required, otherwise 'approved'
        const approvalRequired = state.data?.approval_required || false;
        templateToUpdate.status = approvalRequired ? 'pending' : 'approved';

        state.isSubmittingNotifyManager = false;
      })
      .addCase(notifyManager.rejected, state => {
        state.isSubmittingNotifyManager = false;
      })
      .addCase(clearAvailabilities.pending, state => {
        state.isSubmittingClearAvailability = true;
      })
      .addCase(clearAvailabilities.fulfilled, (state, action) => {
        const templateId = action.payload.templateId;
        const templateToUpdate = state.data!.availability_templates.find(
          template => template.id === templateId
        )!;

        templateToUpdate.availabilities = [];

        state.isSubmittingClearAvailability = false;
      })
      .addCase(clearAvailabilities.rejected, state => {
        state.isSubmittingClearAvailability = false;
      })
      .addCase(createAvailabilityTemplate.pending, state => {
        state.isSubmittingCreateTemplate = true;
      })
      .addCase(createAvailabilityTemplate.fulfilled, (state, action) => {
        // Add the new template to the list of templates
        if (state.data) {
          // If the newly created template is current, update all other templates
          if (action.payload.current) {
            // Set current: false for all existing templates
            state.data.availability_templates.forEach(template => {
              template.current = false;
            });
          }

          state.data.availability_templates.push(action.payload);
          // Set the newly created template as the selected one
          state.selectedTemplateId = action.payload.id;
        }
        state.isSubmittingCreateTemplate = false;
      })
      .addCase(createAvailabilityTemplate.rejected, state => {
        state.isSubmittingCreateTemplate = false;
      })
      .addCase(createNewAvailability.pending, state => {
        state.isSubmittingAvailability = true;
        state.availabilityError = null;
      })
      .addCase(createNewAvailability.fulfilled, (state, action) => {
        state.isSubmittingAvailability = false;

        if (state.data && action.payload && state.selectedTemplateId) {
          // Find the template to update using the currently selected template ID
          const templateToUpdate = state.data.availability_templates.find(
            template => template.id === state.selectedTemplateId
          );

          if (templateToUpdate) {
            templateToUpdate.availabilities = [
              ...templateToUpdate.availabilities,
              action.payload,
            ];
          }
        }
      })
      .addCase(createNewAvailability.rejected, (state, action) => {
        state.isSubmittingAvailability = false;
        state.availabilityError = action.payload as string;
      })
      .addCase(editAvailability.pending, state => {
        state.isSubmittingAvailability = true;
        state.availabilityError = null;
      })
      .addCase(editAvailability.fulfilled, (state, action) => {
        state.isSubmittingAvailability = false;

        if (state.data && action.payload && state.selectedTemplateId) {
          // Find the template to update
          const templateToUpdate = state.data.availability_templates.find(
            template => template.id === state.selectedTemplateId
          );

          if (templateToUpdate) {
            // Replace the updated availability in the array
            const index = templateToUpdate.availabilities.findIndex(
              availability => availability.id === action.payload.id
            );

            if (index !== -1) {
              templateToUpdate.availabilities[index] = action.payload;
            }
          }
        }
      })
      .addCase(editAvailability.rejected, (state, action) => {
        state.isSubmittingAvailability = false;
        state.availabilityError = action.payload as string;
      })
      .addCase(deleteAvailability.pending, state => {
        state.isSubmittingAvailability = true;
        state.availabilityError = null;
      })
      .addCase(deleteAvailability.fulfilled, (state, action) => {
        state.isSubmittingAvailability = false;

        if (state.data && state.selectedTemplateId) {
          const templateToUpdate = state.data.availability_templates.find(
            template => template.id === state.selectedTemplateId
          );

          if (templateToUpdate) {
            templateToUpdate.availabilities =
              templateToUpdate.availabilities.filter(
                availability => availability.id !== action.payload.id
              );
          }
        }
      })
      .addCase(deleteAvailability.rejected, state => {
        state.isSubmittingAvailability = false;
        state.availabilityError = toI18n('errors.generic');
      })
      .addCase(updateTemplateStatus.pending, state => {
        state.isSubmittingAvailability = true;
      })
      .addCase(updateTemplateStatus.fulfilled, (state, action) => {
        state.isSubmittingAvailability = false;

        if (state.data && action.payload) {
          const templateToUpdate = state.data.availability_templates.find(
            template => template.id === action.payload.id
          );

          if (templateToUpdate) {
            // Only update the status field
            templateToUpdate.status = action.payload.status;
          }
        }
      })
      .addCase(updateTemplateStatus.rejected, state => {
        state.isSubmittingAvailability = false;
      });
  },
});

export const { reducer, actions } = myAvailabilitySlice;
export const { setSelectedTemplateId, resetAvailabilityError } = actions;
